/**
 * planBadge — 五档身份徽章（Guest / Free / Lite / Pro / Max）。
 *
 * 唯一真相源：头像下拉状态卡左上角与 Profile 用户名旁共用同一份
 * 徽章 HTML。配色阶梯：Guest 灰 → Free 石板 → Lite 青 → Pro 蓝 →
 * Max 金，一级比一级更"有身份"。
 *
 * 对比度铁律（2026-08-19 修订）：文字与图标描边**永远同色**，且按主题
 * 取对应变体 —— 浅色/阅读主题用同色系加深色，深色主题用亮色。此前
 * 图标描边固定用亮色主色，导致亮色模式下 Guest/Free/Lite 的图标
 * 与背景对比度不足（1.5~2.7:1），文字也只用了勉强达标的 textLight。
 * 现全部档位 × 全部主题的文字+图标对比度 ≥4.5:1（已用 WCAG 相对亮度
 * 逐格核验，见 docs/specs/2026-08-19-avatar-status-card-and-identity-badges.md）。
 *
 * 尊贵阶梯的视觉载体：色相（灰→石板→青→蓝→金）+ 徽章底强度逐档上升
 * （淡染 → 淡染 → 中染 → 强染 → Max 亮色主题实心金底）。
 *
 * 设计约束：
 * - 徽章完全内联样式（含 !important），不依赖任何 CSS 变量/外部规则。
 *   这是头像面板"文字消失但可点击"复现 bug 的防御延续：任何外部
 *   stylesheet（浏览器扩展注入）都无法把徽章隐藏或改色。
 * - SVG 用 Lucide 官方 path 内联（stroke = 该主题变体主色），不新增
 *   静态图标资产文件，避免 assets 清单膨胀。
 * - 纯函数，无 DOM 依赖，可单测。
 */

export type PlanTier = 'guest' | 'free' | 'lite' | 'pro' | 'max';

/** 单个主题下的徽章配色：文字与图标共用 color（SVG stroke = 文字色）。 */
interface BadgeVariant {
  /** 该主题下文字与图标共用的主色。 */
  color: string;
  /** 徽章底（rgba 淡染，或 Max 亮色主题的实色金底）。 */
  bg: string;
  /** 徽章边框（rgba 或实色 hex）。 */
  border: string;
}

interface PlanBadgeStyle {
  label: string;
  icon: string;
  /** 深色主题变体：亮色，凸显档位色。 */
  dark: BadgeVariant;
  /** 浅色/阅读主题变体：同色系加深，保证浅底高对比。 */
  light: BadgeVariant;
}

/** Lucide 图标 path（仅 path 字符串，由 badgeSvg 拼装成完整 SVG）。 */
const ICON_PATHS: Record<string, string> = {
  userRound: '<circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/>',
  bookOpen:
    '<path d="M12 5v16"/><path d="M20.001 19A2 2 0 0022 17V5a2 2 0 00-1.999-2L16 3.002A5 5 0 0012 5a5 5 0 00-4-2H4a2 2 0 00-2 2v12a2 2 0 001.999 2H8a5 5 0 014 2 5 5 0 014-2z"/>',
  medal:
    '<path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15"/><path d="M11 12 5.12 2.2"/><path d="m13 12 5.88-9.8"/><path d="M8 7h8"/><circle cx="12" cy="17" r="5"/><path d="M12 18v-2h-.5"/>',
  gem: '<path d="M10.5 3 8 9l4 13 4-13-2.5-6"/><path d="M17 3a2 2 0 0 1 1.6.8l3 4a2 2 0 0 1 .013 2.382l-7.99 10.986a2 2 0 0 1-3.247 0l-7.99-10.986A2 2 0 0 1 2.4 7.8l2.998-3.997A2 2 0 0 1 7 3z"/><path d="M2 9h20"/>',
  crown: '<path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.735H5.81a1 1 0 0 1-.957-.735L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"/><path d="M5 21h14"/>',
};

export const PLAN_BADGE_STYLES: Record<PlanTier, PlanBadgeStyle> = {
  guest: {
    label: 'Guest',
    icon: 'userRound',
    // round-7: 用户要求 Guest 徽章配色与 Free 完全一致——两者同为"免费档"
    // （Guest=未登录 / Free=登录免费），仅靠图标（UserRound vs BookOpen）与文字区分。
    // 浅色 #546272、暗色 #B0BECF（6.56:1），底染/边框与 Free 逐值相同。
    light: { color: '#546272', bg: 'rgba(84,98,114,0.10)', border: 'rgba(84,98,114,0.38)' },
    dark: { color: '#B0BECF', bg: 'rgba(176,190,207,0.14)', border: 'rgba(176,190,207,0.48)' },
  },
  free: {
    label: 'Free',
    icon: 'bookOpen',
    light: { color: '#546272', bg: 'rgba(84,98,114,0.10)', border: 'rgba(84,98,114,0.38)' },
    // round-5: 用户反馈 Free 在暗色下偏暗。提亮 #94A3B8→#B0BECF（5.11→6.56:1），
    // 中性石板灰；round-7 起 Guest 与之同色（免费档同档，仅图标/文字区分）。
    dark: { color: '#B0BECF', bg: 'rgba(176,190,207,0.14)', border: 'rgba(176,190,207,0.48)' },
  },
  lite: {
    label: 'Lite',
    icon: 'medal',
    // 2026-08-19 round-4: 用户反馈 Lite 在亮/暗都偏淡。亮色加深 #0C6E67→#085E58
    // （4.89→6.01:1），暗色提亮 #2DD4BF→#5EEAD4（6.46→7.60:1），同色系不变。
    light: { color: '#085E58', bg: 'rgba(8,94,88,0.10)', border: 'rgba(8,94,88,0.42)' },
    dark: { color: '#5EEAD4', bg: 'rgba(94,234,212,0.16)', border: 'rgba(94,234,212,0.52)' },
  },
  pro: {
    label: 'Pro',
    icon: 'gem',
    light: { color: '#1D4ED8', bg: 'rgba(29,78,216,0.12)', border: 'rgba(29,78,216,0.50)' },
    dark: { color: '#60A5FA', bg: 'rgba(96,165,250,0.16)', border: 'rgba(96,165,250,0.52)' },
  },
  max: {
    label: 'Max',
    icon: 'crown',
    light: { color: '#6E5400', bg: '#F6E7B4', border: '#A67C00' },
    dark: { color: '#FBBF24', bg: 'rgba(251,191,36,0.22)', border: 'rgba(251,191,36,0.60)' },
  },
};

/** 当前主题是否为深色（avatarView / profileHydrator 共用）。 */
export function resolveDarkTheme(): boolean {
  return (
    typeof document !== 'undefined' &&
    !!document.documentElement &&
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
}

function badgeSvg(icon: string, color: string, size = 12): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' +
    color +
    '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    ICON_PATHS[icon] +
    '</svg>'
  );
}

/**
 * 渲染单个身份徽章 HTML（完全内联样式，防扩展注入）。
 *
 * @param tier 五档身份之一
 * @param dark 深色主题用亮色变体；浅色/阅读主题用加深变体。文字与图标
 *             共用同一变体色（SVG stroke = 文字色），保证任意主题下都可辨。
 * @param extraAttrs 追加到徽章 span 的属性（如 id / data-plan-label）
 */
export function planBadgeHtml(tier: PlanTier, dark: boolean, extraAttrs = ''): string {
  const s = PLAN_BADGE_STYLES[tier];
  const v = dark ? s.dark : s.light;
  return (
    '<span class="plan-id-badge"' +
    extraAttrs +
    ' style="' +
    'color:' + v.color + ' !important;' +
    'background:' + v.bg + ' !important;' +
    'border:1px solid ' + v.border + ' !important;' +
    'display:inline-flex !important;' +
    'align-items:center !important;' +
    'gap:4px !important;' +
    'font-size:11px !important;' +
    'font-weight:650 !important;' +
    'line-height:1 !important;' +
    'padding:2px 8px 2px 5px !important;' +
    'border-radius:980px !important;' +
    'white-space:nowrap !important;' +
    'text-indent:0 !important;' +
    'opacity:1 !important;' +
    'visibility:visible !important;' +
    '">' +
    badgeSvg(s.icon, v.color) +
    s.label +
    '</span>'
  );
}
